import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@uhg-abyss/web/ui/Breadcrumbs';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { config } from '@uhg-abyss/web/tools/config';
import { StarRating } from '@src/common/StarRating';
import {
  useFeedbackSubmission,
  resetFeedback,
} from '@src/hooks/useFeedbackSubmission';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { event } from '@uhg-abyss/web/tools/event';
import { useSelector } from 'react-redux';
import { useDSUserInfo } from '@src/hooks/useDsUserInfo';
import { styled } from '@uhg-abyss/web/tools/styled';
import apiData from '../OrganizationListReviewUrl.json';
import { constants } from '@src/common/Utils/Constants';
import { useUserInfo } from '@src/hooks/useUserInfo';
import { mapBreadcrumbsData } from '@src/common/Utils/utils';

const Wrapper = styled('div', {
  '@screen >= $xs': {
    padding: '16px',
  },
  '@screen >= $xl': {
    padding: '32px 0 0 0',
  },
});

const PageTitle = styled('h1', {
  color: constants.FONT_COLOR_NAVY_BLUE,
  fontSize: '28px !important',
});

const PageText = styled('h4', {
  color: constants.FONT_COLOR_NAVY_BLUE,
  fontWeight: 'bold',
  fontSize: '15px !important',
  lineHeight: '24px !important',
});

const ButtonStyles = styled('div', {
  display: 'flex',
  gap: '10px',
  marginTop: '20px',
});

const RatingStyles = styled('div', {
  paddingBottom: '32px',
});

const TextInputAreaStyle = styled('div', {
  width: '100%',
  '@screen >= $sm': {
    width: '50%',
  },
});

const RatingRequiredError = styled('p', {
  marginBottom: '0px',
  color: 'red',
  fontSize: '12px',
  fontWeight: 'bold',
});

const PageDesc = styled('h4', {
  color: constants.FONT_COLOR_NAVY_BLUE,
  fontSize: '22px !important',
  fontWeight: '700',
  lineHeight: '32px !important',
  letterSpacing: '0px',
  textAlign: 'left',
});

const ButtonMargin = styled('div', {
  marginTop: '16px',
});

export const ApiFeedback = () => {
  const { service } = useParams();
  const [value, setValue] = useState('');
  const [rating, setRating] = useState(0);
  const [displayRatingError, setDisplayRatingError] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const { navigate } = useRouter();
  const [submitFeedbackApi, postSubmitFeedbackApi] = useFeedbackSubmission();
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  const userDSInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const [dsUserInfo, getDSUserInfo] = useDSUserInfo();
  const [, resetFeedbackResponse] = resetFeedback();
  const [, getUserInfo] = useUserInfo();
  const breadcrumbsData = useSelector((state) => {
    return state.BREADCRUMBS.data;
  });

  const goBack = () => {
    navigate('/welcome');
  };

  const capitalizedServiceName = apiData[service]?.feedbackId;

  const resetFeedbackState = () => {
    resetFeedbackResponse();
  };

  const getIsProdFlag = () => {
    if (userDSInfo?.linkedApis?.prod?.includes(capitalizedServiceName)) {
      return true;
    }
    return false;
  };

  const submitFeedback = () => {
    if (rating < 1) {
      setDisplayRatingError(true);
    } else {
      setDisplayRatingError(false);
      const requestBody = {
        apiName: service,
        rating,
        comments: value,
        isProd: getIsProdFlag(),
        orgId: userDSInfo?.orgId,
      };

      postSubmitFeedbackApi({
        variables: { values: requestBody },
        userInfo,
      });
    }
  };

  useEffect(() => {
    resetFeedbackState();
    if (!document.getElementById('auth-script')) {
      const script = document.createElement('script');

      script.src = config('UHC_AUTH_SCRIPT');
      script.async = false;
      script.id = 'auth-script';

      document.head.appendChild(script);
    }

    if (userInfo.uuid === '') {
      getUserInfo();
    }

    if (userDSInfo?.uuid === '') {
      getDSUserInfo({ userInfo });
    }
  }, []);

  useEffect(() => {
    // Use the breadcrumbs data from Redux
    if (breadcrumbsData && Array.isArray(breadcrumbsData)) {
      const items = mapBreadcrumbsData(breadcrumbsData);
      setBreadcrumbItems(items);
    }
  }, [breadcrumbsData]); // Only re-run when breadcrumbs data or URL changes

  useEffect(() => {
    if (
      userDSInfo?.uuid !== '' &&
      !userDSInfo?.linkedApis?.stage?.includes(capitalizedServiceName)
    ) {
      goBack();
    }

    if (!dsUserInfo.loading && dsUserInfo.data.uuid !== '') {
      event('NEW_PAGE_LOAD', {
        pageName: `apimarketplace:welcome:${service}-feedback`,
        siteSectionL1: 'welcome',
        siteSectionL2: `${service}-feedback`,
        dsUserInfo: dsUserInfo.data,
        userInfo,
      });
    }
  }, [service, dsUserInfo]);

  useEffect(() => {
    setDisplayRatingError(false);
  }, [rating]);

  const form = useForm();
  return (
    <Wrapper>
      <Breadcrumbs
        size="11px"
        css={{
          fontWeight: 400,
          'abyss-breadcrumb-current': { fontWeight: 400 },
          '.abyss-breadcrumb-link': {
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
          },
        }}
        items={breadcrumbItems}
      />
      <PageTitle>{apiData[service]?.name}</PageTitle>
      {!submitFeedbackApi.loading &&
        submitFeedbackApi?.data?.status &&
        submitFeedbackApi?.data?.status !== 200 && (
          <Alert title="Your submission has failed. Please try again." />
        )}
      {!submitFeedbackApi.loading && submitFeedbackApi?.data?.status === 200 ? (
        <React.Fragment>
          <PageDesc>Thank you for your feedback.</PageDesc>
          <ButtonMargin>
            <Button fontSize="15px" onClick={goBack}>
              Back to Account Dashboard
            </Button>
          </ButtonMargin>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <PageText>How likely are you to refer this API to a peer?</PageText>
          <FormProvider state={form} onSubmit={submitFeedback}>
            <RatingStyles>
              <StarRating value={setRating} />
              {displayRatingError && (
                <RatingRequiredError>
                  Please provide your API rating.
                </RatingRequiredError>
              )}
            </RatingStyles>
            <TextInputAreaStyle>
              <TextInputArea
                model="feedback"
                rows={6}
                label="Do you have any feedback for how we can improve this API?"
                value={value}
                placeholder="Please provide your feedback for this API."
                maxLength={config('FEEDBACK_CHARACTER_LIMIT')}
                onChange={(e) => {
                  return setValue(e.target.value);
                }}
                css={{
                  'abyss-text-input-area-label': {
                    color: '$primary1',
                    fontSize: '14px',
                  },
                }}
              />
            </TextInputAreaStyle>
            <ButtonStyles>
              <Button size="$md" variant="outline" onClick={goBack}>
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={submitFeedbackApi.loading}
                ariaLoadingLabel="Submitting Feedback"
              >
                Submit
              </Button>
            </ButtonStyles>
          </FormProvider>
        </React.Fragment>
      )}
    </Wrapper>
  );
};
